import { useEffect, useRef, useState } from 'react'
import WebViewer, { WebViewerInstance, Core as CoreType } from '@pdftron/webviewer'
import { useParams, useNavigate } from 'react-router'
import { env } from '~/env.ts'
import { headerCustomizations } from '~/utilities/webviewer/customizations'
import { JobManifest } from '~/graphql/hooks/useJobManifestQuery'
import { useQuery } from '@tanstack/react-query'
import {
  getWorkflowStepResult,
  WorkflowStepBoundingBoxResult
} from '~/lib/mbue-api/services/workflowStepResult'

import Loading from '~/components/ui/Loading'
import {
  useFetchDocList,
  useUpdateWorkflowStep,
  useUpdateStepTaskResult,
  useFetchWorkflow
} from '~/hooks/submittals'
import Button from '~/components/ui/Button'
import { toast } from 'sonner'
import { LoaderCircleIcon } from 'lucide-react'
import { zoomToArea } from '~/utilities/webviewer/zoomToArea'
import { WorkflowStepStatus } from 'mbue-rest-client'
import { useFlag } from '~/hooks/useFlag'
import { getFirefoxVersion } from '~/utilities/utils'

type Annotation = CoreType.Annotations.Annotation

interface BoundingBoxSelected {
  pageNumber: number
  boundingBox: {
    xmin: number
    xmax: number
    ymin: number
    ymax: number
  }
}

export default function ScheduleSelection() {
  const navigate = useNavigate()
  const viewerDivRef = useRef<HTMLDivElement>(null)
  const webviewerInstanceRef = useRef<WebViewerInstance | null>(null)
  const isViewerInitialized = useRef<boolean>(false)
  const [boundingBoxSelected, setBoundingboxSelected] =
    useState<BoundingBoxSelected | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const isProdViewerEnabled = useFlag('apryse-prod-viewer-enabled')

  const [workflowStepResult, setWorkflowStepResult] =
    useState<WorkflowStepBoundingBoxResult | null>(null)

  const { projectId, workflowInstanceId } = useParams<{
    projectId: string
    workflowInstanceId: string
  }>()

  const { data: workflowWithData, isLoading: workflowWithDataIsLoading } =
    useFetchWorkflow({
      projectId: parseInt(projectId!, 10),
      workflowInstanceId: workflowInstanceId!
    })

  const { data: docList, isLoading: isLoadingDocList } = useFetchDocList({
    projectId: parseInt(projectId!, 10)
  })

  const { data: workflowStepResults, isLoading: isLoadingWorkflowStepResult } = useQuery({
    queryKey: ['workflowStepResult', projectId, workflowInstanceId, 1],
    queryFn: () =>
      getWorkflowStepResult({
        projectId: parseInt(projectId!, 10),
        workflowInstanceId: workflowInstanceId!,
        stepIndex: 1
      }),
    enabled: !!projectId && !!workflowInstanceId
  })

  const { mutateAsync: updateStepTaskResult, isPending: isPendingUpdateStepTaskResult } =
    useUpdateStepTaskResult(parseInt(projectId!, 10), workflowInstanceId!, 1, 0)

  const { mutate: updateWorkflowStep, isPending: isPendingUpdateWorkflowStep } =
    useUpdateWorkflowStep({
      onSuccess: async () => {
        await updateStepTaskResult({
          status: true
        })
        toast.success('Product Table updated successfully')
        navigate(-1)
      },
      onError: (error) => {
        console.error('Error:', error)
        toast.success('Error updating product table')
      }
    })

  useEffect(() => {
    if (isLoadingWorkflowStepResult || !workflowStepResults) return

    setWorkflowStepResult(workflowStepResults[0] as WorkflowStepBoundingBoxResult)

    if (!workflowStepResult) return

    if (workflowStepResult.result_type !== 'bounding_box') return

    const pageIndex = workflowStepResult?.document_bounding_box?.page_ix
    const boundingBox = workflowStepResult?.document_bounding_box.bounding_box

    if (!pageIndex || !boundingBox) return

    setBoundingboxSelected({
      pageNumber: pageIndex,
      boundingBox
    })
  }, [workflowStepResult, isLoadingWorkflowStepResult, workflowStepResults])

  useEffect(() => {
    if (isProdViewerEnabled === null) return

    if (
      isLoadingDocList ||
      isLoadingWorkflowStepResult ||
      workflowWithDataIsLoading ||
      !workflowStepResult ||
      !workflowStepResults
    )
      return

    let webviewerKey: string = env.VITE_WEBVIEWER_DEMO_TOKEN ?? ''

    if (isProdViewerEnabled && env.VITE_WEBVIEWER_TOKEN) {
      webviewerKey = env.VITE_WEBVIEWER_TOKEN
    }

    const documentUri = docList?.drawing_doc?.download_url?.[0] ?? ''

    let webviewerInstance: WebViewerInstance

    const initWebviewer = async () => {
      if (isViewerInitialized.current) return
      try {
        webviewerInstance = await WebViewer.Iframe(
          {
            path: '/webviewer-0ad25b9a',
            licenseKey: webviewerKey,
            preloadWorker: 'pdf',
            fullAPI: true,
            ui: 'legacy'
          },
          viewerDivRef.current!
        )

        webviewerInstanceRef.current = webviewerInstance

        const { UI, Core } = webviewerInstance

        const version = getFirefoxVersion()

        if (version && version > 145) {
          console.log('Disabling virtual display mode for Firefox version', version)
          const displayMode = Core.documentViewer.getDisplayModeManager()
          displayMode.disableVirtualDisplayMode()
        }

        const { documentViewer, annotationManager, Annotations } = Core
        const { iframeWindow } = UI

        const snippingTool = documentViewer.getTool(Core.Tools.ToolNames.SNIPPING)
        UI.setLayoutMode(Core.DisplayModes.Single.toString())

        documentViewer.addEventListener('documentLoaded', async () => {
          setDefaultViewerSettings(webviewerInstance)

          const pageCount = documentViewer.getPageCount()

          const schedulePage =
            pageCount === 1
              ? 1
              : (workflowStepResult?.document_bounding_box?.page_ix ?? 1)

          const boundingBox = workflowStepResult?.document_bounding_box.bounding_box

          documentViewer.setCurrentPage(schedulePage, false)

          await new Promise((resolve) => setTimeout(resolve, 1000))

          const $snippingPopup: HTMLDivElement | null =
            iframeWindow.document.querySelector('.SnippingPopupContainer')

          if ($snippingPopup) $snippingPopup.style.display = 'none'

          await new Promise((resolve) => setTimeout(resolve, 500))

          headerCustomizations(
            webviewerInstance,
            { matches: [] } as unknown as JobManifest,
            'bounding-box',
            documentViewer.getPageCount(),
            { isLayerToggleEnabled: false }
          )

          documentViewer.setFitMode(documentViewer.FitMode.FitPage)

          if (Object.values(boundingBox).every((value) => value > 0)) {
            await new Promise((resolve) => setTimeout(resolve, 500))

            // Preselected Annotation
            // Semi-transparent black (adjust alpha 0.0 to 1.0)
            const backdropColor = new Annotations.Color(0, 0, 0, 0.4)

            const rect = new Core.Math.Rect(
              Math.min(boundingBox.xmin, boundingBox.xmax),
              Math.min(boundingBox.ymin, boundingBox.ymax),
              Math.max(boundingBox.xmin, boundingBox.xmax),
              Math.max(boundingBox.ymin, boundingBox.ymax)
            )

            const pageW = documentViewer.getPageWidth(schedulePage)
            const pageH = documentViewer.getPageHeight(schedulePage)

            const backdropAnnotations = []
            const baseBackdropId = `temp-backdrop-${Date.now()}`

            const createBackdropRect = (
              idSuffix: string,
              x1: number,
              y1: number,
              x2: number,
              y2: number
            ) => {
              const backdropRectAnnot = new Annotations.RectangleAnnotation()
              backdropRectAnnot.PageNumber = schedulePage
              backdropRectAnnot.setRect(new Core.Math.Rect(x1, y1, x2, y2))
              backdropRectAnnot.FillColor = backdropColor
              backdropRectAnnot.StrokeThickness = 0 // No border for backdrop parts
              backdropRectAnnot.NoResize = true
              backdropRectAnnot.NoMove = true
              backdropRectAnnot.ReadOnly = true
              backdropRectAnnot.Locked = true
              backdropRectAnnot.Author = 'System'
              backdropRectAnnot.Id = `${baseBackdropId}-${idSuffix}`
              return backdropRectAnnot
            }

            // 1. Top Rectangle
            if (rect.y2 < pageH) {
              // Only add if there's space above
              backdropAnnotations.push(
                createBackdropRect('top', 0, rect.y2, pageW, pageH)
              )
            }
            // 2. Bottom Rectangle
            if (rect.y1 > 0) {
              // Only add if there's space below
              backdropAnnotations.push(createBackdropRect('bottom', 0, 0, pageW, rect.y1))
            }
            // 3. Left Rectangle
            if (rect.x1 > 0) {
              // Only add if there's space to the left
              backdropAnnotations.push(
                createBackdropRect('left', 0, rect.y1, rect.x1, rect.y2)
              )
            }
            // 4. Right Rectangle
            if (rect.x2 < pageW) {
              // Only add if there's space to the right
              backdropAnnotations.push(
                createBackdropRect('right', rect.x2, rect.y1, pageW, rect.y2)
              )
            }

            const tempGuide = new Annotations.RectangleAnnotation() // Use Rectangle for a border
            tempGuide.PageNumber = schedulePage
            tempGuide.setRect(rect)
            tempGuide.StrokeColor = new Annotations.Color(34, 136, 255, 0.8) // Apryse Blue, semi-transparent
            tempGuide.StrokeThickness = 2
            tempGuide.NoResize = false
            tempGuide.NoMove = false
            tempGuide.ReadOnly = false // Prevent easy selection/modification
            tempGuide.Locked = false
            tempGuide.Id = `temp-guide-${Date.now()}` // Unique ID
            tempGuide.Author = 'System' // Differentiate from user annotations
            tempGuide.IsClickableOutsideRect = false
            tempGuide.ToolName = 'SnippingTool'
            // Add the annotation without selecting it
            annotationManager.addAnnotations([...backdropAnnotations, tempGuide], {
              autoFocus: false
            })

            await annotationManager.drawAnnotations({ pageNumber: schedulePage })

            zoomToArea(
              webviewerInstance,
              schedulePage,
              boundingBox.xmin,
              boundingBox.ymin,
              boundingBox.xmax,
              boundingBox.ymax
            )
          }
          setIsLoading(false)
        })

        documentViewer.addEventListener(
          'toolModeUpdated',
          async (currentTool: { name: string }) => {
            await new Promise((resolve) => setTimeout(resolve, 0))
            if (currentTool.name === 'SnippingTool') {
              const $snippingPopup: HTMLDivElement | null =
                iframeWindow.document.querySelector('.SnippingPopupContainer')

              if ($snippingPopup) $snippingPopup.style.display = 'none'
            }
          }
        )

        documentViewer.addEventListener('pageNumberUpdated', () => {
          documentViewer.setFitMode(documentViewer.FitMode.FitPage)
        })

        snippingTool.addEventListener('annotationAdded', (annot: Annotation) => {
          const rect = annot.getRect()

          setBoundingboxSelected({
            pageNumber: annot.getPageNumber(),
            boundingBox: {
              xmin: rect.x1,
              xmax: rect.x2,
              ymin: rect.y1,
              ymax: rect.y2
            }
          })
        })

        annotationManager.addEventListener(
          'annotationChanged',
          (annots: Annotation[], action: string) => {
            if (action !== 'modify') return

            annots.forEach((annot: Annotation) => {
              if (annot.Subject !== 'Rectangle') return
              const rect = annot.getRect()

              setBoundingboxSelected({
                pageNumber: annot.getPageNumber(),
                boundingBox: {
                  xmin: rect.x1,
                  xmax: rect.x2,
                  ymin: rect.y1,
                  ymax: rect.y2
                }
              })
            })
          }
        )

        void documentViewer.loadDocument(documentUri, { extension: 'pdf' })
      } catch (e) {
        console.warn('There was an error initializing the webviewer.', e)
      }
    }

    initWebviewer()
      .catch(() => {
        alert('Error loading the viewer. please reload the page')
      })
      .finally(() => {
        isViewerInitialized.current = true
      })
  }, [
    docList,
    isProdViewerEnabled,
    isLoadingDocList,
    workflowStepResult,
    isLoadingWorkflowStepResult,
    workflowWithDataIsLoading,
    workflowStepResults
  ])

  const hasShownCompletionToastRef = useRef(false)

  useEffect(() => {
    if (workflowWithDataIsLoading) return

    if (
      !workflowWithDataIsLoading &&
      workflowWithData?.steps?.[1]?.state?.status === WorkflowStepStatus.COMPLETED &&
      !hasShownCompletionToastRef.current
    ) {
      toast.success('Step Completed', {
        description: 'This is a read only version of the schedule'
      })
      hasShownCompletionToastRef.current = true
    }
  }, [workflowWithData, workflowWithDataIsLoading])

  const handleEditSource = () => {
    if (!webviewerInstanceRef.current) return

    const { UI, Core } = webviewerInstanceRef.current
    const { annotationManager } = Core

    const annotations = annotationManager.getAnnotationsList()
    if (annotations.length > 0) {
      annotationManager.deleteAnnotations(annotations, { imported: false, force: true })
    }

    UI.setToolMode(Core.Tools.ToolNames.SNIPPING)
  }

  const handleConfirm = () => {
    if (
      !projectId ||
      !workflowInstanceId ||
      !docList?.drawing_doc?.id ||
      !boundingBoxSelected
    ) {
      toast.error(
        'Project ID, Workflow Instance ID, Drawing Doc ID, and a selected area are required to update the step'
      )
      return
    }

    const data = workflowStepResult!

    updateWorkflowStep({
      projectId: parseInt(projectId, 10),
      workflowInstanceId,
      stepIndex: 1,
      result: {
        result_type: 'bounding_box',
        document_bounding_box: {
          doc_id: docList?.drawing_doc?.id,
          page_ix: boundingBoxSelected.pageNumber,
          bounding_box_coordinate_type: 'PDF',
          bounding_box: boundingBoxSelected.boundingBox
        },
        matched_schedule_found: true,
        processing_metadata: data.processing_metadata
      }
    })
  }

  const isPending = isPendingUpdateWorkflowStep || isPendingUpdateStepTaskResult

  const isStepCompleted =
    workflowWithData?.steps?.[1]?.state?.status === WorkflowStepStatus.COMPLETED

  return (
    <>
      {isLoading && <Loading mode="dark" />}
      <div className="h-[calc(100vh-122px)] w-full viewer" ref={viewerDivRef} />
      <div className="min-h-[58px] flex flex-row items-center gap-2 pt-2 mb-2 px-4">
        <div className="text-sm w-4/6 text-wrap">
          First, confirm this is the correct source for the products. Click Confirm or
          Edit as needed. Use the window selector to ensure all product content is
          visible.
        </div>
        <div className="flex gap-2 flex-grow justify-end">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            Cancel
          </Button>
          <Button variant="outline" onClick={handleEditSource} disabled={isStepCompleted}>
            Edit Source
          </Button>
          <Button
            variant="default"
            onClick={handleConfirm}
            disabled={isPending || isStepCompleted}
          >
            {isPending && <LoaderCircleIcon className="w-4 h-4 animate-spin" />}
            Confirm & Continue
          </Button>
        </div>
      </div>
    </>
  )
}

export function setDefaultViewerSettings(instance: WebViewerInstance) {
  const { UI } = instance
  UI.setTheme('dark')

  const iframeDoc = UI.iframeWindow.document
  const pageNavOverlay: HTMLDivElement | null = iframeDoc.querySelector(
    '[data-element="pageNavOverlay"]'
  )

  if (pageNavOverlay) {
    pageNavOverlay.style.display = 'none'
  }
}
